import { gql } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'
import { useEffect, useMemo } from 'react'
import {
  ADMIN_TAB_COUNTS,
  ADMIN_TAB_COUNTS_VARIABLES,
  IAdminTabCounts,
  IAdminTabCountsArgs,
  sumAdminTabCounts
} from 'src/components/admin/adminTabCounts'
import { useDashboardFilterContextOptional } from 'src/components/Layouts/DashboardLayout/DashboardFilterContext'
import useLogger from 'src/context/LoggerProvider/useLogger'
import { useAuthProvider } from '../context/AuthProvider'
import {
  CheckoutType,
  IQuery,
  IQueryInventoryReviewStatusArgs,
  IQueryReservationsArgs,
  IQueryUnreadMessagesArgs
} from '../types/graphql'

const GET_INFO = gql`
  query Dashboard(
    $pagination: PaginationInput!
    $searchInput: SearchCheckoutsInput!
  ) {
    reservations(pagination: $pagination, searchInput: $searchInput) {
      total
    }
    rents(pagination: $pagination, searchInput: $searchInput) {
      total
    }

    unreadMessages {
      total
    }
    missingFacturaCount(searchInput: $searchInput)
    missingRefundDocCount(searchInput: $searchInput)
    pendingClosedDealCount
  }
`

/**
 * Deliberately a SEPARATE operation from `GET_INFO` — the monthly
 * inventory-review badge is a late addition (see InventoryReview feature)
 * and a missing/rolled-back server resolver for `inventoryReviewStatus`
 * must never break the rest of the dashboard notification counts. Errors
 * are swallowed at `logger.debug`, not `logger.error` (see the effect
 * below) — this badge failing is not Datadog-alert-worthy.
 */
const GET_INVENTORY_REVIEW = gql`
  query InventoryReviewBadge($input: InventoryReviewScopeInput) {
    inventoryReviewStatus(input: $input) {
      period
      pendingCount
    }
  }
`

export type INotificationCounts = {
  profile: number
  fiscal: number
  fiscalUrl: string | null
  locations: number
  rents: number
  reservations: number
  messages: number
  missingFacturas: number
  missingRefundDocs: number
  /**
   * Number of host-side off-platform commission closed deals with at least
   * one OPEN (unpaid) invoice — due or not. Drives the host notification
   * trail — sidebar 'Tratos cerrados' badge, profile-button red dot, and
   * the payment banner on the detail page. Backed by
   * `billing.hasOpenInvoice`, maintained by the host-billing-dunning cron
   * sweep + invoice webhooks; clears when every invoice is settled.
   */
  pendingClosedDeals: number
  /**
   * Sum of the admin panel's tab badges (`sumAdminTabCounts`) — the same
   * `ADMIN_TAB_COUNTS` operation the panel polls, so the nav badge and the
   * panel tabs always agree. 0 for non-admins.
   */
  admin: number
  /**
   * Count of scopes (personal + teams) with an open, uncompleted monthly
   * inventory-review task — drives the sidebar 'Inventario' badge, the
   * profile-button red dot, and the "Hoy" dashboard banner. Backed by a
   * standalone `inventoryReviewStatus` query (see `GET_INVENTORY_REVIEW`
   * above) — defaults to 0 on any query error so a missing resolver never
   * breaks the rest of the badge set.
   */
  inventoryReviewPending: number
  total: number
}

const useNotificationCounts = (): INotificationCounts => {
  const { currentUser } = useAuthProvider()
  const logger = useLogger()
  // Only present when the hook is rendered inside DashboardLayout. Outside
  // (LandingNav drawer, ProfileButton on public pages) it's null and the
  // badges keep their legacy cross-team total. Inside, we narrow the badge
  // counts to the user's selected team filter so they line up with the
  // per-page tab counts.
  const dashFilter = useDashboardFilterContextOptional()
  const teamFilterReady = !!dashFilter?.filterReady
  const teamScope = useMemo(
    () => (teamFilterReady ? dashFilter!.buildFilter('checkouts') : null),
    [teamFilterReady, dashFilter]
  )

  const [getTotals, { data, error: totalsError }] = useLazyQuery<
    Pick<
      IQuery,
      | 'reservations'
      | 'unreadMessages'
      | 'rents'
      | 'missingFacturaCount'
      | 'missingRefundDocCount'
      | 'pendingClosedDealCount'
    >,
    IQueryReservationsArgs & IQueryUnreadMessagesArgs
  >(GET_INFO, {
    fetchPolicy: 'cache-and-network'
  })

  useEffect(() => {
    if (totalsError) {
      logger.error('Error retrieving total info', totalsError)
    }
  }, [totalsError, logger])

  // Shares its cache entry with the admin panel's tab strip, so the panel's
  // network-only refetch + 60 s poll keeps this badge live while it is open.
  const [getAdminTotals, { data: dataAdmin, error: adminTotalsError }] =
    useLazyQuery<IAdminTabCounts, IAdminTabCountsArgs>(ADMIN_TAB_COUNTS, {
      fetchPolicy: 'cache-and-network'
    })

  useEffect(() => {
    if (adminTotalsError) {
      logger.error('Error retrieving admin total info', adminTotalsError)
    }
  }, [adminTotalsError, logger])

  const [
    getInventoryReview,
    { data: inventoryReviewData, error: inventoryReviewError }
  ] = useLazyQuery<
    Pick<IQuery, 'inventoryReviewStatus'>,
    IQueryInventoryReviewStatusArgs
  >(GET_INVENTORY_REVIEW, {
    fetchPolicy: 'cache-and-network'
  })

  // Deliberately `logger.debug`, not `logger.error` — see the comment on
  // `GET_INVENTORY_REVIEW` above. A missing/rolled-back resolver here must
  // never trip a Datadog alert; `inventoryReviewPending` just falls back to 0.
  useEffect(() => {
    if (inventoryReviewError) {
      logger.debug('inventoryReviewStatus unavailable', inventoryReviewError)
    }
  }, [inventoryReviewError, logger])

  // Refetch when team filter changes so the badge stays in sync with the
  // per-page tabs. When dashFilter is absent (non-dashboard context), the
  // dependency array still works — teamScope stays null.
  useEffect(() => {
    if (!currentUser) return

    getTotals({
      variables: {
        pagination: { from: 0, pageSize: 0 },
        searchInput: { type: CheckoutType.Pending, ...(teamScope ?? {}) }
      }
    })

    // `InventoryReviewScopeInput` only accepts `teamId` / `userAndTeams` —
    // narrower than `DashboardFilter` (which can also carry `landlordId` /
    // `userId` for the admin host-selected path). Pick only the two fields
    // the input type actually declares; spreading the raw teamScope would
    // send an unknown field and fail GraphQL input validation.
    // Admin viewing a specific host (dashboard host-switcher) → the badge
    // reflects that host's pending count; the server allows admins to target
    // any user's personal scope. Non-admins never send `userId`.
    const inventoryHostId =
      dashFilter?.isAdmin && dashFilter.selectedHostId
        ? dashFilter.selectedHostId
        : null
    getInventoryReview({
      variables: {
        input: inventoryHostId
          ? { userId: inventoryHostId }
          : teamScope
            ? {
                teamId: teamScope.teamId,
                userAndTeams: teamScope.userAndTeams
              }
            : undefined
      }
    })

    if (currentUser.isAdmin) {
      getAdminTotals({ variables: ADMIN_TAB_COUNTS_VARIABLES })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentUser,
    teamScope?.teamId,
    teamScope?.userAndTeams,
    teamScope?.landlordId
  ])

  const value = useMemo((): INotificationCounts => {
    const { isVerified, requestedVerification, accountVerificationError } =
      currentUser ?? {}
    const needsToVerify = currentUser && !isVerified && !requestedVerification
    const profile = needsToVerify || accountVerificationError ? 1 : 0
    // Was `userLocations(filter: ALL, rejected: true)` inside the admin-only
    // query: for an admin with no userId the server answers PLATFORM-WIDE
    // (52 rejected listings on 2026-09-30), which pinned the admin's red dot
    // on and badged "Espacios" with other hosts' listings. Hosts never ran
    // that query, so they always saw 0 — kept at 0 for everyone.
    const locations = 0
    const rents = data?.reservations?.total ?? 0
    const reservations = data?.rents?.total ?? 0
    const messages = data?.unreadMessages?.total ?? 0
    const missingFacturas = data?.missingFacturaCount ?? 0
    const missingRefundDocs = data?.missingRefundDocCount ?? 0
    const pendingClosedDeals = data?.pendingClosedDealCount ?? 0

    const fc = currentUser?.fiscalCompliance
    const isAdmin = !!currentUser?.isAdmin
    // Gate on `shouldShowFiscalWarning`, NOT raw `!isComplete`. The backend sets
    // it to false for corporate/broker hosts (their personal CURP/RFC doesn't
    // gate retention — the team's fiscal info does), so a pure team-host no
    // longer sees a profile-button fiscal badge for missing personal fields.
    const fiscal = !isAdmin && fc?.shouldShowFiscalWarning ? 1 : 0
    const fiscalUrl =
      !isAdmin && fc?.shouldShowFiscalWarning ? fc.completeProfileUrl : null

    const admin = isAdmin ? sumAdminTabCounts(dataAdmin) : 0

    const inventoryReviewPending =
      inventoryReviewData?.inventoryReviewStatus?.pendingCount ?? 0

    return {
      profile,
      fiscal,
      fiscalUrl,
      locations,
      rents,
      reservations,
      messages,
      missingFacturas,
      missingRefundDocs,
      pendingClosedDeals,
      admin,
      inventoryReviewPending,
      total:
        rents +
        reservations +
        messages +
        admin +
        profile +
        locations +
        fiscal +
        missingFacturas +
        missingRefundDocs +
        pendingClosedDeals +
        inventoryReviewPending
    }
  }, [data, dataAdmin, inventoryReviewData, currentUser])

  return value
}

export default useNotificationCounts
